import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Badge, Calendar, Input, Separator, Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue, Popover, PopoverContent, PopoverTrigger, } from '@cargomatic/ui';
import { format, parse, isValid } from 'date-fns';
import { CalendarIcon, PlusCircle, MoveDown } from 'lucide-react';
import { debounce } from 'lodash';
import { cn } from 'utils/ui/classes';
import { sluggify } from 'utils/localization/utils';
const types = {
    EQUALS: 'equals',
    AFTER: 'after',
    BEFORE: 'before',
    BETWEEN: 'range',
};
export function DataTableDateFilter({ column, title, className }) {
    var _a, _b, _c, _d;
    const today = new Date();
    const { t } = useTranslation();
    const value = column === null || column === void 0 ? void 0 : column.getFilterValue();
    const [type, setType] = useState((_a = value === null || value === void 0 ? void 0 : value.type) !== null && _a !== void 0 ? _a : types.BEFORE);
    const [dateFrom, setDateFrom] = useState((_b = value === null || value === void 0 ? void 0 : value.dateFrom) !== null && _b !== void 0 ? _b : today);
    const [dateTo, setDateTo] = useState((_c = value === null || value === void 0 ? void 0 : value.dateTo) !== null && _c !== void 0 ? _c : today);
    const [dateOn, setDateOn] = useState((_d = value === null || value === void 0 ? void 0 : value.dateOn) !== null && _d !== void 0 ? _d : today);
    const [month, setMonth] = useState(today);
    const [open, setOpen] = useState(false);
    function populateDateFields() {
        var _a, _b, _c, _d;
        setType((_a = value === null || value === void 0 ? void 0 : value.type) !== null && _a !== void 0 ? _a : types.BEFORE);
        setDateFrom((_b = value === null || value === void 0 ? void 0 : value.dateFrom) !== null && _b !== void 0 ? _b : today);
        setDateTo((_c = value === null || value === void 0 ? void 0 : value.dateTo) !== null && _c !== void 0 ? _c : today);
        setDateOn((_d = value === null || value === void 0 ? void 0 : value.dateOn) !== null && _d !== void 0 ? _d : today);
    }
    useEffect(() => {
        populateDateFields();
        if (!value) {
            setType(types.BEFORE);
        }
    }, [value]);
    function handleOpenChange(open) {
        setOpen(open);
        if (!open && !value) {
            populateDateFields();
        }
    }
    function isApplyButtonDisabled() {
        if ((type === types.BEFORE && dateTo) ||
            (type === types.AFTER && dateFrom) ||
            (type === types.EQUALS && dateOn)) {
            return false;
        }
        return !(type && dateFrom && dateTo);
    }
    function isDateFilterSet() {
        return (value === null || value === void 0 ? void 0 : value.dateFrom) || (value === null || value === void 0 ? void 0 : value.dateTo) || (value === null || value === void 0 ? void 0 : value.dateOn);
    }
    function handleApply() {
        const filter = {};
        if (type === types.EQUALS)
            filter.dateOn = dateOn;
        if (type === types.BEFORE || type === types.BETWEEN)
            filter.dateTo = dateTo; // why does this line pass the linter when the following doesn't?
        if (type === types.AFTER || type === types.BETWEEN)
            filter.dateFrom = dateFrom;
        filter.type = type;
        column === null || column === void 0 ? void 0 : column.setFilterValue(filter);
        setOpen(false);
    }
    function handleSelectDateRange(selected) {
        const { dateFrom, dateTo } = selected || {};
        setDateFrom(dateFrom);
        setDateTo(dateTo);
    }
    function handleReset() {
        setDateFrom();
        setDateTo();
        setDateOn();
        column.setFilterValue(undefined);
    }
    function formatLongDateString(date) {
        return format(date, 'PPP');
    }
    function formatDateFilter() {
        if (type === types.AFTER && value.dateFrom) {
            return `${t(sluggify('After'))} ${formatLongDateString(value.dateFrom)}`;
        }
        if (type === types.BEFORE && value.dateTo) {
            return `${t(sluggify('Before'))} ${formatLongDateString(value.dateTo)}`;
        }
        if (type === types.EQUALS && value.dateOn) {
            return `${t(sluggify('Equals'))} ${formatLongDateString(value.dateOn)}`;
        }
        if (value.dateFrom && value.dateTo) {
            const dateRangeString = [
                formatLongDateString(value.dateFrom),
                formatLongDateString(value.dateTo),
            ].join(' - ');
            return `${t(sluggify('Between'))} ${dateRangeString}`;
        }
    }
    return (React.createElement(Popover, { modal: false, open: open, onOpenChange: handleOpenChange },
        React.createElement(PopoverTrigger, { asChild: true },
            React.createElement(Button, { variant: "outline", size: "sm", className: "border-dashed flex items-center" },
                React.createElement(PlusCircle, { className: "mr-2 h-4 w-4" }),
                title,
                isDateFilterSet() && (React.createElement(React.Fragment, null,
                    React.createElement(Separator, { orientation: "vertical", className: "mx-2 h-4" }),
                    React.createElement(Badge, { className: "rounded-sm px-1" }, formatDateFilter()))))),
        React.createElement(PopoverContent, { className: cn('overflow-y-auto space-y-2', className), align: "start" },
            React.createElement(Select, { id: "type", onValueChange: setType, value: type, className: "" },
                React.createElement(SelectTrigger, { className: "" },
                    React.createElement(SelectValue, { placeholder: t(sluggify('Select')) })),
                React.createElement(SelectContent, null,
                    React.createElement(SelectGroup, null,
                        React.createElement(SelectLabel, null, "Type"),
                        Object.entries(types).map(([slug, value]) => (React.createElement(SelectItem, { key: slug, value: value }, t(slug))))))),
            type === types.EQUALS && (React.createElement(SingleDayPicker, { selected: dateOn, onSelect: setDateOn, month: month, onMonthChange: setMonth })),
            type === types.AFTER && (React.createElement(SingleDayPicker, { selected: dateFrom, onSelect: setDateFrom, month: month, onMonthChange: setMonth })),
            type === types.BEFORE && (React.createElement(SingleDayPicker, { selected: dateTo, onSelect: setDateTo, month: month, onMonthChange: setMonth })),
            type === types.BETWEEN && (React.createElement(DayRangePicker, { selected: { dateFrom, dateTo }, onSelect: handleSelectDateRange, month: month, onMonthChange: setMonth })),
            React.createElement("hr", { className: "" }),
            React.createElement("div", { className: "sm:flex sm:items-center sm:space-x-2 gap-2 sm:justify-end" },
                React.createElement(Button, { variant: "", onClick: handleApply, className: "w-full sm:w-auto", disabled: isApplyButtonDisabled() }, t(sluggify('Apply'))),
                React.createElement(Button, { variant: "secondary", onClick: handleReset, className: "w-full sm:w-auto" }, t(sluggify('Clear')))))));
}
const SingleDayPicker = ({ selected, onSelect, month: inputMonth, onMonthChange, }) => {
    const [date, setDate] = useState(selected);
    const [month, setMonth] = useState(inputMonth || new Date());
    const handleSelect = (date) => {
        setDate(date);
        onSelect(date);
    };
    const handleMonthChange = (date) => {
        setMonth(date);
        onMonthChange(date);
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(DateInput, { date: date, onChange: handleSelect, onMonthChange: handleMonthChange }),
        React.createElement(Calendar, { month: month, onMonthChange: handleMonthChange, mode: "single", selected: selected, onSelect: handleSelect, initialFocus: true })));
};
const DayRangePicker = ({ selected, onSelect, month: inputMonth, onMonthChange, }) => {
    const { dateFrom, dateTo } = selected;
    const [from, setFrom] = useState(dateFrom);
    const [to, setTo] = useState(dateTo);
    const [month, setMonth] = useState(inputMonth || new Date());
    function handleSelect(selected, _, modifiers = {}) {
        const { from: dateFrom, to: dateTo } = selected || {};
        // handling cases where the user re-selects a date
        if (modifiers.selected && !modifiers.range_middle) {
            if (modifiers.range_start) {
                setFrom();
            }
            if (modifiers.range_end) {
                setTo();
            }
        }
        else {
            setFrom(dateFrom);
            setTo(dateTo);
            onSelect({ dateFrom, dateTo });
        }
    }
    const handleMonthChange = (date) => {
        setMonth(date);
        onMonthChange(date);
    };
    useEffect(() => {
        if (from !== dateFrom || to !== dateTo)
            handleSelect({ from, to });
        // handling cases where 'from' and 'to' need to swap
        if (from && to && from > to) {
            setFrom(to);
            setTo(from);
        }
    }, [to, from]);
    return (React.createElement(React.Fragment, null,
        React.createElement(DateInput, { date: from, onChange: setFrom, onMonthChange: handleMonthChange, placeholder: "Enter an after date" }),
        React.createElement("div", { className: "grid justify-items-center" },
            React.createElement(MoveDown, { className: cn('h-4', (!from || !to) && 'text-muted-foreground') })),
        React.createElement(DateInput, { date: to, onChange: setTo, onMonthChange: handleMonthChange, placeholder: "Enter a before date" }),
        React.createElement(Calendar, { month: month, onMonthChange: handleMonthChange, mode: "range", selected: { to, from }, onSelect: handleSelect, initialFocus: true })));
};
const DateInput = ({ date, onChange, onMonthChange, placeholder = 'Enter a date', }) => {
    const { t } = useTranslation();
    const [input, setInput] = useState(date ? format(date, 'P') : '');
    useEffect(() => {
        setInput(date ? format(date, 'P') : ''); // Update input when date changes
    }, [date]);
    const handleInputChange = (e) => {
        setInput(e.target.value);
    };
    // Debounce the input changes with a delay of 300ms
    const debouncedInputChange = debounce((value) => {
        let parsedDate = parse(value, 'P', new Date());
        parsedDate = isValid(parsedDate)
            ? parsedDate
            : parse(value, 'PPP', new Date());
        if (isValid(parsedDate) && parsedDate >= new Date(0)) {
            onChange(parsedDate);
            onMonthChange === null || onMonthChange === void 0 ? void 0 : onMonthChange(parsedDate);
        }
        else if (value === '') {
            onChange(undefined);
        }
    }, 100);
    useEffect(() => {
        debouncedInputChange(input);
        return () => {
            debouncedInputChange.cancel();
        };
    }, [input]);
    return (React.createElement("div", { className: "relative" },
        React.createElement(CalendarIcon, { className: cn('absolute left-2.5 top-1/3 h-1/3 w-auto', !date && 'text-muted-foreground') }),
        React.createElement(Input, { variant: "outline", className: cn('p-1.5 pl-7 border rounded w-full font-normal', !date && 'text-muted-foreground'), placeholder: t(sluggify(placeholder)), value: input, onChange: handleInputChange })));
};
